import { useState, useRef } from "react";
import { Upload, X, Image } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ImageUploaderProps {
  value: string | string[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  error?: string;
  label?: string;
  required?: boolean;
}

const ImageUploader = ({
  value,
  onChange,
  multiple = false,
  error = "",
  label = "Image",
  required = false,
}: ImageUploaderProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();
  const images = multiple
    ? Array.isArray(value)
      ? value
      : value
        ? [value]
        : []
    : value
      ? [value as string]
      : [];

  const handleFileChange = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const readers = fileArray.map((file) => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers).then((results) => {
      if (multiple) {
        onChange([...images, ...results]);
      } else {
        onChange(results[0]);
      }
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const removeImage = (index: number) => {
    if (multiple) {
      const newImages = images.filter((_, i) => i !== index);
      onChange(newImages);
    } else {
      onChange("");
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div>
      <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-2">
        {label} {required && "*"}
      </label>

      {/* Upload Area */}
      <div
        onClick={openFileDialog}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`relative border-2 border-dashed rounded-lg p-6 sm:p-8 text-center cursor-pointer transition-all duration-300 ${
          isDragging
            ? "border-black bg-gray-50"
            : error
              ? "border-red-500"
              : "border-gray-300 hover:border-black"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          onChange={(e) => handleFileChange(e.target.files)}
          className="hidden"
        />

        <div className="flex flex-col items-center gap-3">
          <div className="bg-gray-100 rounded-full p-3 sm:p-4">
            <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600" />
          </div>
          <div>
            <p className="text-sm sm:text-base font-semibold text-gray-700">
              {isDragging
                ? t("Drop images here")
                : t("Click to upload or drag and drop")}
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {t("PNG, JPG, GIF up to 10MB")}{" "}
              {multiple && t("(Multiple files allowed)")}
            </p>
          </div>
        </div>
      </div>

      {error && <p className="mt-2 text-xs sm:text-sm text-red-500">{error}</p>}

      {/* Image Previews */}
      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
          {images.map((img, index) => (
            <div
              key={index}
              className="relative group rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50"
            >
              <div className="aspect-square">
                <img
                  src={img}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Remove Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeImage(index);
                }}
                className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600 z-10"
                title="Remove image"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 pointer-events-none" />
            </div>
          ))}
        </div>
      )}

      {images.length === 0 && !error && (
        <div className="mt-4 flex items-center justify-center gap-2 text-gray-400 text-sm">
          <Image className="w-4 h-4" />
          <span>{t("No image uploaded yet")}</span>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
